import { call, put } from 'redux-saga/effects'
import { ACTIONS, APIS } from '../../_config'
import { CallApi } from '../api/callApi'
import { ErrorResponse, SuccessResponse } from '../api/apiTypes'
import { getZoneDesignDetailType, getZonType } from '../api/hooks/types/experimentApiTypes'
import { serialize } from '../../_common/functions'
import { stopTiming, trackError } from '../../utils/analytics/rumTracking'

export function* createDesign<P, R>(action: { type: string; payload: P; onSuccess: (response: SuccessResponse<R>) => void; onError: (response: ErrorResponse<R>) => void }): any {
    try {
        const resp = yield call(CallApi.POST, APIS.EXPERIMENT.CREATE_DESIGN, action.payload, true)
        if (resp.status >= 200 && resp.status < 300 && resp.data) {
            yield put({
                type: ACTIONS.EXPERIMENT,
                payload: resp.data.data,
            })
            stopTiming('wizard_total', { experiment_type: resp.data?.data?.experiment_type ?? (action.payload as any)?.experiment_type ?? '' })
            action?.onSuccess(resp.data as SuccessResponse<R>)
        } else {
            action?.onError(resp.data as ErrorResponse<R>)
        }
    } catch (e: any) {
        // action?.onError(e?.response?.data as ErrorResponse<R>)
        // ✅ CHANGED: Added null check and fallback error object
        action?.onError(
            e?.response?.data || {
                message: e?.message || 'Failed to create design',
                status_code: e?.status || 500,
                success: false,
            },
        )
        trackError(e instanceof Error ? e : new Error(e?.message || 'Saga error'), { saga: 'createDesign', action: action?.type })
    }
}

export function* updateExperiment<P, R>(action: {
    type: string
    payload: {
        literal: string
        [name: string]: any
    }
    onSuccess: (response: SuccessResponse<R>) => void
    onError: (response: ErrorResponse<R>) => void
}): any {
    try {
        const literal: string = action?.payload?.literal
        const data: any = action.payload
        delete data.literal

        if (data?.design_table_filters?.sql_query) {
            data.design_table_filters.sql_query = btoa(data.design_table_filters.sql_query)
        }
        if (data?.measurement_table_filters?.sql_query) {
            data.measurement_table_filters.sql_query = btoa(data.measurement_table_filters.sql_query)
        }
        if (data?.intervention_table_filters?.sql_query) {
            data.intervention_table_filters.sql_query = btoa(data.intervention_table_filters.sql_query)
        }
        if (data?.recommendation_table_filters?.sql_query) {
            data.recommendation_table_filters.sql_query = btoa(data.recommendation_table_filters.sql_query)
        }

        const resp = yield call(CallApi.PUT, APIS.EXPERIMENT.UPDATE_EXPERIMENT(literal), data, true)
        if (resp.status >= 200 && resp.status < 300 && resp.data) {
            yield put({
                type: ACTIONS.EXPERIMENT,
                payload: Array.isArray(resp.data.data) ? resp.data.data[0] : resp.data.data,
            })

            action?.onSuccess(resp.data as SuccessResponse<R>)
        } else {
            action?.onError(resp.data as ErrorResponse<R>)
        }
    } catch (e: any) {
        // action?.onError(e?.response?.data)
        // ✅ CHANGED: Added null check and fallback error object
        action?.onError(
            e?.data || {
                message: e?.message || 'Failed to update experiment',
                status_code: e?.status || 500,
                success: false,
            },
        )
        trackError(e instanceof Error ? e : new Error(e?.message || 'Saga error'), { saga: 'updateExperiment', action: action?.type })
    }
}

export function* getzonedesign<R>(action: { type: string; payload: getZonType; onSuccess: (response: SuccessResponse<R>) => void; onError: (response: ErrorResponse<R>) => void }): any {
    try {
        const resp = yield call(
            CallApi.GET,
            APIS.EXPERIMENT.ZONE_DESIGN(action?.payload?.zone),

            action.payload,
            false,
        )
        if (resp.status >= 200 && resp.status < 300 && resp.data) {
            action?.onSuccess(resp.data as SuccessResponse<R>)
        } else {
            action?.onError(resp.data as ErrorResponse<R>)
        }
    } catch (e: any) {
        // action?.onError(e.data)
        // ✅ CHANGED: Added null check and fallback error object
        action?.onError(
            e?.data || {
                message: e?.message || 'Failed to fetch zone design',
                status_code: e?.status || 500,
                success: false,
            },
        )
        trackError(e instanceof Error ? e : new Error(e?.message || 'Saga error'), { saga: 'getzonedesign', action: action?.type })
    }
}

export function* getuserdesign<P, R>(action: { type: string; payload: P; onSuccess: (response: SuccessResponse<R>) => void; onError: (response: ErrorResponse<R>) => void }): any {
    try {
        const resp = yield call(
            CallApi.GET,
            APIS.EXPERIMENT.USER_DESIGN,

            action.payload,
            false,
        )
        if (resp.status >= 200 && resp.status < 300 && resp.data) {
            action?.onSuccess(resp.data as SuccessResponse<R>)
        } else {
            action?.onError(resp.data as ErrorResponse<R>)
        }
    } catch (e: any) {
        // action?.onError(e.data)
        // ✅ CHANGED: Added null check and fallback error object
        action?.onError(
            e?.data || {
                message: e?.message || 'Failed to fetch user design',
                status_code: e?.status || 500,
                success: false,
            },
        )
        trackError(e instanceof Error ? e : new Error(e?.message || 'Saga error'), { saga: 'getuserdesign', action: action?.type })
    }
}

export function* getExperiments<P, R>(action: { type: string; payload: any; onSuccess: (response: SuccessResponse<R>) => void; onError: (response: ErrorResponse<R>) => void }): any {
    try {
        const { page, per_page, sort_by } = action.payload
        const queryObject: any = {}
        if (page) queryObject['page'] = page
        if (per_page) queryObject['per_page'] = per_page
        if (sort_by) queryObject['sort_by'] = sort_by

        const APIURL = APIS.EXPERIMENT.GET_EXPERIMENTS + `?${new URLSearchParams(queryObject).toString()}`

        const resp = yield call(CallApi.POST, APIURL, action.payload, true)
        if (resp.status >= 200 && resp.status < 300 && resp.data) {
            action?.onSuccess(resp.data as SuccessResponse<R>)
        } else {
            action?.onError(resp.data as ErrorResponse<R>)
        }
    } catch (e: any) {
        // action?.onError(e.data ?? e)
        // ✅ CHANGED: Added null check and fallback error object
        action?.onError(
            e?.data || {
                message: e?.message || 'Failed to fetch experiments',
                status_code: e?.status || 500,
                success: false,
            },
        )
        trackError(e instanceof Error ? e : new Error(e?.message || 'Saga error'), { saga: 'getExperiments', action: action?.type })
    }
}

export function* getZoneDesignDetail<P, R>(action: {
    type: string
    payload: getZoneDesignDetailType

    onSuccess: (response: SuccessResponse<R>) => void
    onError: (response: ErrorResponse<R>) => void
}): any {
    try {
        const resp = yield call(CallApi.GET, APIS.EXPERIMENT.ZONE_DESIGN_DETAILS(action?.payload?.table_name), action.payload, false)
        if (resp.status >= 200 && resp.status < 300 && resp.data) {
            action?.onSuccess(resp.data as SuccessResponse<R>)
        } else {
            action?.onError(resp.data as ErrorResponse<R>)
        }
    } catch (e: any) {
        // action?.onError(e.data)
        // ✅ CHANGED: Added null check and fallback error object
        action?.onError(
            e?.data || {
                message: e?.message || 'Failed to fetch zone design detail',
                status_code: e?.status || 500,
                success: false,
            },
        )
        trackError(e instanceof Error ? e : new Error(e?.message || 'Saga error'), { saga: 'getZoneDesignDetail', action: action?.type })
    }
}

export function* getExperimentDetails<P, R>(action: {
    type: string
    payload: {
        literal: string
        use_orm?: boolean
    }
    onSuccess: (response: SuccessResponse<R>) => void
    onError: (response: ErrorResponse<R>) => void
}): any {
    try {
        const params: any = {}
        if (action.payload.use_orm) {
            params['use_orm'] = action.payload.use_orm
        }
        const resp = yield call(CallApi.GET, APIS.EXPERIMENT.GET_EXPERIMENT_DETAILS(action.payload.literal), params, false, true)
        if (resp.status >= 200 && resp.status < 300 && resp.data) {
            yield put({
                type: ACTIONS.EXPERIMENT,
                payload: resp.data.data,
            })
            action?.onSuccess(resp.data)
        } else {
            action?.onError(resp)
        }
    } catch (e: any) {
        // action?.onError(e)
        // ✅ CHANGED: Added null check and fallback error object
        action?.onError(
            e?.data || {
                message: e?.message || 'Failed to fetch experiment details',
                status_code: e?.status || 500,
                success: false,
            },
        )
        trackError(e instanceof Error ? e : new Error(e?.message || 'Saga error'), { saga: 'getExperimentDetails', action: action?.type })
    }
}

export function* getContaminationCheck<R>(action: {
    type: string
    payload: {
        literal: string
    }
    onSuccess: (response: SuccessResponse<R>) => void
    onError: (response: ErrorResponse<R>) => void
}): any {
    try {
        const resp = yield call(CallApi.GET, APIS.EXPERIMENT.GET_CONTAMINATION_CHECK(action.payload.literal), {}, false, true)
        if (resp.status >= 200 && resp.status < 300 && resp.data) {
            action?.onSuccess(resp.data as SuccessResponse<R>)
        } else {
            action?.onError(resp.data as ErrorResponse<R>)
        }
    } catch (e: any) {
        action?.onError(
            e?.data || {
                message: e?.message || 'Failed to fetch contamination data',
                status_code: e?.status || 500,
                success: false,
            },
        )
    }
}

export function* postContaminationSnapshot<R>(action: {
    type: string
    payload: {
        literal: string
    }
    onSuccess: (response: SuccessResponse<R>) => void
    onError: (response: ErrorResponse<R>) => void
}): any {
    try {
        const resp = yield call(CallApi.POST, APIS.EXPERIMENT.POST_CONTAMINATION_SNAPSHOT(action.payload.literal), action.payload, true)
        if (resp.status >= 200 && resp.status < 300 && resp.data) {
            action?.onSuccess(resp.data as SuccessResponse<R>)
        } else {
            action?.onError(resp.data as ErrorResponse<R>)
        }
    } catch (e: any) {
        action?.onError(
            e?.data || {
                message: e?.message || 'Failed to post contamination snapshot',
                status_code: e?.status || 500,
                success: false,
            },
        )
    }
}

export function* updateContaminationSelection<R>(action: {
    type: string
    payload: {
        literal: string
        selected_experiment_ids: number[]
        expected_snapshot_revision: number
    }
    onSuccess: (response: SuccessResponse<R>) => void
    onError: (response: ErrorResponse<R>) => void
}): any {
    try {
        const resp = yield call(CallApi.PUT, APIS.EXPERIMENT.UPDATE_CONTAMINATION_SELECTION(action.payload.literal), action.payload, true)
        if (resp.status >= 200 && resp.status < 300 && resp.data) {
            action?.onSuccess(resp.data as SuccessResponse<R>)
        } else {
            action?.onError(resp.data as ErrorResponse<R>)
        }
    } catch (e: any) {
        action?.onError(
            e?.data || {
                message: e?.message || 'Failed to update contamination selection',
                status_code: e?.status || 500,
                success: false,
            },
        )
    }
}

export function* deleteContaminationSnapshot<R>(action: {
    type: string
    payload: {
        literal: string
    }
    onSuccess: (response: SuccessResponse<R>) => void
    onError: (response: ErrorResponse<R>) => void
}): any {
    try {
        const resp = yield call(CallApi.DELETE, APIS.EXPERIMENT.DELETE_CONTAMINATION_SNAPSHOT(action.payload.literal), action.payload, true)
        if (resp.status >= 200 && resp.status < 300 && resp.data) {
            action?.onSuccess(resp.data as SuccessResponse<R>)
        } else {
            action?.onError(resp.data as ErrorResponse<R>)
        }
    } catch (e: any) {
        action?.onError(
            e?.data || {
                message: e?.message || 'Failed to delete contamination snapshot',
                status_code: e?.status || 500,
                success: false,
            },
        )
    }
}

export function* getExperimentDetailsWatch<P, R>(action: {
    type: string
    payload: {
        literal: string
        use_orm?: boolean
        n?: number
    }
    onSuccess: (response: SuccessResponse<R>) => void
    onError: (response: ErrorResponse<R>) => void
}): any {
    try {
        const params: any = {}
        if (action.payload.use_orm) {
            params['use_orm'] = action.payload.use_orm
            params['n'] = action.payload.n
        }
        const resp = yield call(CallApi.GET, APIS.EXPERIMENT.GET_EXPERIMENT_DETAILS(action.payload.literal), params, false, true)
        if (resp.status >= 200 && resp.status < 300 && resp.data) {
            action?.onSuccess(resp.data.data)
        } else {
            action?.onError(resp)
        }
    } catch (e: any) {
        // action?.onError(e.data)
        // ✅ CHANGED: Added null check and fallback error object
        action?.onError(
            e?.data || {
                message: e?.message || 'Failed to fetch experiment details',
                status_code: e?.status || 500,
                success: false,
            },
        )
        trackError(e instanceof Error ? e : new Error(e?.message || 'Saga error'), { saga: 'getExperimentDetailsWatch', action: action?.type })
    }
}

export function* getotherexperiments<P, R>(action: { type: string; payload: P; onSuccess: (response: SuccessResponse<R>) => void; onError: (response: ErrorResponse<R>) => void }): any {
    try {
        const resp = yield call(
            CallApi.GET,
            APIS.EXPERIMENT.GET_EXPERIMENTS_OTHERS,

            action.payload,
            false,
        )
        if (resp.status >= 200 && resp.status < 300 && resp.data) {
            action?.onSuccess(resp.data as SuccessResponse<R>)
        } else {
            action?.onError(resp.data as ErrorResponse<R>)
        }
    } catch (e: any) {
        // action?.onError(e.data)
        // ✅ CHANGED: Added null check and fallback error object
        action?.onError(
            e?.data || {
                message: e?.message || 'Failed to fetch other experiments',
                status_code: e?.status || 500,
                success: false,
            },
        )
        trackError(e instanceof Error ? e : new Error(e?.message || 'Saga error'), { saga: 'getotherexperiments', action: action?.type })
    }
}

export function* getColumnPreviwData<P, R>(action: {
    type: string
    payload: {
        literal: string
        [name: string]: any
    }
    onSuccess: (response: SuccessResponse<R>) => void
    onError: (response: ErrorResponse<R>) => void
}): any {
    try {
        const literal: string = action?.payload?.literal
        const data: any = action.payload
        delete data.literal

        const resp = yield call(CallApi.GET, APIS.EXPERIMENT.GET_COLUMN_PREVIEW_DATA(literal), data, false, true)
        if (resp.status >= 200 && resp.status < 300 && resp.data) {
            action?.onSuccess(resp.data as SuccessResponse<R>)
        } else {
            action?.onError(resp.data as ErrorResponse<R>)
        }
    } catch (e: any) {
        // action?.onError(e.data)
        // ✅ CHANGED: Added null check and fallback error object
        action?.onError(
            e?.data || {
                message: e?.message || 'Failed to fetch column preview data',
                status_code: e?.status || 500,
                success: false,
            },
        )
        trackError(e instanceof Error ? e : new Error(e?.message || 'Saga error'), { saga: 'getColumnPreviwData', action: action?.type })
    }
}

export function* filterPreivewData<P, R>(action: {
    type: string
    payload: {
        literal: string
        [name: string]: any
    }
    onSuccess: (response: SuccessResponse<R>) => void
    onError: (response: ErrorResponse<R>) => void
}): any {
    try {
        const literal = action.payload.literal
        const page = action.payload.page
        const per_page = action.payload.per_page
        const table_type = action.payload.table_type

        const urlParams: any = { literal, page, per_page }
        if (table_type) {
            urlParams.table_type = table_type
        }

        const data: any = action.payload
        delete data.literal
        delete data.page
        delete data.per_page
        if (table_type) {
            delete data.table_type
        }
        if (data.sql_query) {
            data.sql_query = btoa(data.sql_query)
        }

        const resp = yield call(CallApi.POST, APIS.EXPERIMENT.FILTER_PREVIEW_DATA + '?' + serialize(urlParams), data, true)
        if (resp.status >= 200 && resp.status < 300 && resp.data) {
            action?.onSuccess(resp.data as SuccessResponse<R>)
        } else {
            action?.onError(resp.data as ErrorResponse<R>)
        }
    } catch (e: any) {
        // action?.onError(e?.response?.data?.message as ErrorResponse<R>)
        // ✅ CHANGED: Added null check and fallback error object
        action?.onError(
            e?.response?.data?.message || {
                message: e?.message || 'Failed to fetch filtered preview data',
                status_code: e?.status || 500,
                success: false,
            },
        )
        trackError(e instanceof Error ? e : new Error(e?.message || 'Saga error'), { saga: 'filterPreivewData', action: action?.type })
    }
}

export function* createUserDesign<P, R>(action: {
    type: string
    payload: {
        literal: string
        [name: string]: any
    }
    onSuccess: (response: SuccessResponse<R>) => void
    onError: (response: ErrorResponse<R>) => void
}): any {
    try {
        const literal = action.payload.literal

        const data: any = action.payload
        delete data.literal
        const resp = yield call(CallApi.POST, `${APIS.EXPERIMENT.USER_DESIGN_CREATE}?literal=${literal}`, data, true)
        if (resp.status >= 200 && resp.status < 300 && resp.data) {
            action?.onSuccess(resp.data as SuccessResponse<R>)
        } else {
            action?.onError(resp.data as ErrorResponse<R>)
        }
    } catch (e: any) {
        // action?.onError(e.data)
        // ✅ CHANGED: Added null check and fallback error object
        action?.onError(
            e?.data || {
                message: e?.message || 'Failed to create user design',
                status_code: e?.status || 500,
                success: false,
            },
        )
        trackError(e instanceof Error ? e : new Error(e?.message || 'Saga error'), { saga: 'createUserDesign', action: action?.type })
    }
}
export function* removeExperiment<P, R>(action: {
    type: string
    payload: {
        literal: string
        reason: string | null
    }
    onSuccess: (response: SuccessResponse<R>) => void
    onError: (response: ErrorResponse<R>) => void
}): any {
    try {
        const resp = yield call(CallApi.DELETE, APIS.EXPERIMENT.REMOVE_EXPERIMENT(action.payload.literal, action.payload?.reason), { reason: action.payload?.reason }, true)
        if (resp.status >= 200 && resp.status < 300 && resp.data) {
            action?.onSuccess(resp.data as SuccessResponse<R>)
        } else {
            action?.onError(resp.data as ErrorResponse<R>)
        }
    } catch (e: any) {
        // action?.onError(e.data)
        // ✅ CHANGED: Added null check and fallback error object
        action?.onError(
            e?.data || {
                message: e?.message || 'Failed to remove experiment',
                status_code: e?.status || 500,
                success: false,
            },
        )
        trackError(e instanceof Error ? e : new Error(e?.message || 'Saga error'), { saga: 'removeExperiment', action: action?.type })
    }
}

export function* cancelExperiment<P, R>(action: {
    type: string
    payload: {
        literal: string
        step: string
    }
    onSuccess: (response: SuccessResponse<R>) => void
    onError: (response: ErrorResponse<R>) => void
}): any {
    try {
        const resp = yield call(CallApi.POST, APIS.EXPERIMENT.CANCEL_EXPERIMENT(action.payload.literal) + '?step=' + action.payload.step, {}, true)
        if (resp.status >= 200 && resp.status < 300 && resp.data) {
            action?.onSuccess(resp.data as SuccessResponse<R>)
        } else {
            action?.onError(resp.data as ErrorResponse<R>)
        }
    } catch (e: any) {
        // action?.onError(e.data)
        // ✅ CHANGED: Added null check and fallback error object
        action?.onError(
            e?.data || {
                message: e?.message || 'Failed to cancel experiment',
                status_code: e?.status || 500,
                success: false,
            },
        )
        trackError(e instanceof Error ? e : new Error(e?.message || 'Saga error'), { saga: 'cancelExperiment', action: action?.type })
    }
}

export function* uploadExperimentfile<P, R>(action: {
    type: string
    payload: {
        literal: string
        [name: string]: any
    }
    onSuccess: (response: SuccessResponse<R>) => void
    onError: (response: ErrorResponse<R>) => void
}): any {
    try {
        const literal: string = action?.payload?.literal
        const data: any = action.payload?.formData

        const resp = yield call(CallApi.POST, APIS.EXPERIMENT.UPLOAD_FILE(literal), data, true, (progrss: any) => {
            if (action?.payload?.progress) {
                action?.payload?.progress(progrss)
            }
        })

        if (resp.status >= 200 && resp.status < 300 && resp.data) {
            action?.onSuccess(resp.data as SuccessResponse<R>)
        } else {
            action?.onError(resp.data as ErrorResponse<R>)
        }
    } catch (e: any) {
        // action?.onError(e.data)
        // ✅ CHANGED: Added null check and fallback error object
        action?.onError(
            e?.data || {
                message: e?.message || 'Failed to upload experiment file',
                status_code: e?.status || 500,
                success: false,
            },
        )
        trackError(e instanceof Error ? e : new Error(e?.message || 'Saga error'), { saga: 'uploadExperimentfile', action: action?.type })
    }
}

export function* uploadRecurringMeasurementFile<P, R>(action: {
    type: string
    payload: {
        literal: string
        [name: string]: any
    }
    onSuccess: (response: SuccessResponse<R>) => void
    onError: (response: ErrorResponse<R>) => void
}): any {
    try {
        const literal: string = action?.payload?.literal
        const data: any = action.payload?.formData

        const resp = yield call(CallApi.POST, APIS.EXPERIMENT.UPLOAD_RECURRING_MEASUREMENT_FILE(literal), data, true, (progrss: any) => {
            if (action?.payload?.progress) {
                action?.payload?.progress(progrss)
            }
        })

        if (resp.status >= 200 && resp.status < 300 && resp.data) {
            action?.onSuccess(resp.data as SuccessResponse<R>)
        } else {
            action?.onError(resp.data as ErrorResponse<R>)
        }
    } catch (e: any) {
        action?.onError(e.data)
        trackError(e instanceof Error ? e : new Error(e?.message || 'Saga error'), { saga: 'uploadRecurringMeasurementFile', action: action?.type })
    }
}
export function* recurringFileProcessWatch<P, R>(action: {
    type: string
    payload: {
        literal: string
        [name: string]: any
    }
    onSuccess: (response: SuccessResponse<R>) => void
    onError: (response: ErrorResponse<R>) => void
}): any {
    try {
        const { literal, n } = action.payload
        const resp = yield call(CallApi.GET, APIS.EXPERIMENT.RECURRING_FILE_PROCESS_WATCH(literal), { n }, true)

        if (resp.status >= 200 && resp.status < 300 && resp.data) {
            action?.onSuccess(resp.data as SuccessResponse<R>)
        } else {
            action?.onError(resp.data as ErrorResponse<R>)
        }
    } catch (e: any) {
        action?.onError(e.data)
        trackError(e instanceof Error ? e : new Error(e?.message || 'Saga error'), { saga: 'recurringFileProcessWatch', action: action?.type })
    }
}

export function* uploadAdditionalDataFile<P, R>(action: {
    type: string
    payload: {
        literal: string
        [name: string]: any
    }
    onSuccess: (response: SuccessResponse<R>) => void
    onError: (response: ErrorResponse<R>) => void
}): any {
    try {
        const literal: string = action?.payload?.literal
        const data: any = action.payload?.formData

        const resp = yield call(CallApi.POST, APIS.EXPERIMENT.UPLOAD_ADDITIONAL_DATA(literal), data, true, (progrss: any) => {
            if (action?.payload?.progress) {
                action?.payload?.progress(progrss)
            }
        })

        if (resp.status >= 200 && resp.status < 300 && resp.data) {
            action?.onSuccess(resp.data as SuccessResponse<R>)
        } else {
            action?.onError(resp.data as ErrorResponse<R>)
        }
    } catch (e: any) {
        // action?.onError(e.data)
        // ✅ CHANGED: Added null check and fallback error object
        action?.onError(
            e?.data || {
                message: e?.message || 'Failed to upload additional data file',
                status_code: e?.status || 500,
                success: false,
            },
        )
        trackError(e instanceof Error ? e : new Error(e?.message || 'Saga error'), { saga: 'uploadAdditionalDataFile', action: action?.type })
    }
}

export function* mapAdditionalDataFile<P, R>(action: {
    type: string
    payload: {
        literal: string
        file_name: string
        granularity_column_name: string
    }
    onSuccess: (response: SuccessResponse<R>) => void
    onError: (response: ErrorResponse<R>) => void
}): any {
    try {
        const literal: string = action?.payload?.literal
        const data: any = action.payload
        const resp = yield call(CallApi.GET, APIS.EXPERIMENT.MAP_ADDITIONAL_DATA(literal), data, false, true)

        if (resp.status >= 200 && resp.status < 300 && resp.data) {
            action?.onSuccess(resp.data as SuccessResponse<R>)
        } else {
            action?.onError(resp.data as ErrorResponse<R>)
        }
    } catch (e: any) {
        // action?.onError(e.data)
        // ✅ CHANGED: Added null check and fallback error object
        action?.onError(
            e?.data || {
                message: e?.message || 'Failed to map additional data file',
                status_code: e?.status || 500,
                success: false,
            },
        )
        trackError(e instanceof Error ? e : new Error(e?.message || 'Saga error'), { saga: 'mapAdditionalDataFile', action: action?.type })
    }
}

export function* getRctGroupResultS<P, R>(action: {
    type: string
    payload: {
        literal: string
        [name: string]: any
    }
    onSuccess: (response: SuccessResponse<R>) => void
    onError: (response: ErrorResponse<R>) => void
}): any {
    try {
        const literal: string = action?.payload?.literal
        const data: any = action.payload?.formData
        const resp = yield call(CallApi.GET, APIS.EXPERIMENT.GET_RCT_GROUP_RESULTS(literal), data, false, true)

        if (resp.status >= 200 && resp.status < 300 && resp.data) {
            action?.onSuccess(resp.data as SuccessResponse<R>)
        } else {
            action?.onError(resp.data as ErrorResponse<R>)
        }
    } catch (e: any) {
        // action?.onError(e.data)
        // ✅ CHANGED: Added null check and fallback error object
        action?.onError(
            e?.data || {
                message: e?.message || 'Failed to get RCT group results',
                status_code: e?.status || 500,
                success: false,
            },
        )
        trackError(e instanceof Error ? e : new Error(e?.message || 'Saga error'), { saga: 'getRctGroupResultS', action: action?.type })
    }
}
export function* exportRctGroupResultS<P, R>(action: {
    type: string
    payload: {
        literal: string
        [name: string]: any
    }
    onSuccess: (response: SuccessResponse<R>) => void
    onError: (response: ErrorResponse<R>) => void
}): any {
    try {
        const literal: string = action?.payload?.literal
        const data: any = action.payload?.formData
        const resp = yield call(CallApi.GET, APIS.EXPERIMENT.EXPORT_RCT_GROUP_RESULTS(literal), data, false, true)

        if (resp.status >= 200 && resp.status < 300 && resp.data) {
            action?.onSuccess(resp.data as SuccessResponse<R>)
        } else {
            action?.onError(resp.data as ErrorResponse<R>)
        }
    } catch (e: any) {
        // action?.onError(e.data)
        // ✅ CHANGED: Added null check and fallback error object
        action?.onError(
            e?.data || {
                message: e?.message || 'Failed to export RCT group results',
                status_code: e?.status || 500,
                success: false,
            },
        )
        trackError(e instanceof Error ? e : new Error(e?.message || 'Saga error'), { saga: 'exportRctGroupResultS', action: action?.type })
    }
}

export function* processvalidatadata<P, R>(action: {
    type: string
    payload: {
        literal: string
        upload_type: string
        adls_file_path_recurring?: string
        recurring_file_name?: string
    }
    onSuccess: (response: SuccessResponse<R>) => void
    onError: (response: ErrorResponse<R>) => void
}): any {
    const { literal, upload_type, adls_file_path_recurring, recurring_file_name } = action.payload

    try {
        const resp = yield call(CallApi.GET, APIS.EXPERIMENT.PROCESS_VALIDATED_DATA(literal, upload_type, adls_file_path_recurring, recurring_file_name), {}, false, true)
        if (resp.status >= 200 && resp.status < 300 && resp.data) {
            action?.onSuccess(resp.data as SuccessResponse<R>)
        } else {
            action?.onError(resp.data as ErrorResponse<R>)
        }
    } catch (e: any) {
        // action?.onError({
        //     message: e.message,
        //     status_code: 400,
        //     success: false,
        // })
        // ✅ CHANGED: Added null check and fallback error object
        action?.onError(
            e?.data || {
                message: e?.message || 'Failed to process validated data',
                status_code: e?.status || 500,
                success: false,
            },
        )
        trackError(e instanceof Error ? e : new Error(e?.message || 'Saga error'), { saga: 'processvalidatadata', action: action?.type })
    }
}

export function* getmeasurementdata<P, R>(action: {
    type: string
    payload: {
        literal: string
        [name: string]: any
    }
    onSuccess: (response: SuccessResponse<R>) => void
    onError: (response: ErrorResponse<R>) => void
}): any {
    try {
        const page = action.payload.page
        const per_page = action.payload.per_page
        const urlParams: any = { page, per_page }
        const data: any = action.payload
        delete data.page
        delete data.per_page

        const resp = yield call(CallApi.POST, APIS.EXPERIMENT.MEASUREMENT_DATA + '?' + serialize(urlParams), data, true)
        if (resp.status >= 200 && resp.status < 300 && resp.data) {
            action?.onSuccess(resp.data.data as SuccessResponse<R>)
        } else {
            action?.onError(resp.data as ErrorResponse<R>)
        }
    } catch (e: any) {
        // action?.onError(e.data)
        // ✅ CHANGED: Added null check and fallback error object
        action?.onError(
            e?.data || {
                message: e?.message || 'Failed to fetch measurement data',
                status_code: e?.status || 500,
                success: false,
            },
        )
        trackError(e instanceof Error ? e : new Error(e?.message || 'Saga error'), { saga: 'getmeasurementdata', action: action?.type })
    }
}

export function* getCardData<P, R>(action: { type: string; payload: any; onSuccess: (response: SuccessResponse<R>) => void; onError: (response: ErrorResponse<R>) => void }): any {
    try {
        const resp = yield call(CallApi.POST, APIS.EXPERIMENT.CARDS, action.payload)
        if (resp.status >= 200 && resp.status < 300 && resp.data) {
            action?.onSuccess(resp.data as SuccessResponse<R>)
        } else {
            action?.onError(resp.data as ErrorResponse<R>)
        }
    } catch (e: any) {
        // action?.onError(e.data)
        // ✅ CHANGED: Added null check and fallback error object
        action?.onError(
            e?.data || {
                message: e?.message || 'Failed to fetch card data',
                status_code: e?.status || 500,
                success: false,
            },
        )
        trackError(e instanceof Error ? e : new Error(e?.message || 'Saga error'), { saga: 'getCardData', action: action?.type })
    }
}

export function* getDMLCardData<P, R>(action: { type: string; payload: any; onSuccess: (response: SuccessResponse<R>) => void; onError: (response: ErrorResponse<R>) => void }): any {
    try {
        const literal: string = action.payload.literal
        const resp = yield call(CallApi.POST, APIS.EXPERIMENT.DML_DATA(literal), action.payload)
        if (resp.status >= 200 && resp.status < 300 && resp.data) {
            action?.onSuccess(resp.data as SuccessResponse<R>)
        } else {
            action?.onError(resp.data as ErrorResponse<R>)
        }
    } catch (e: any) {
        action?.onError(
            e?.data || {
                message: e?.message || 'Failed to fetch DML card data',
                status_code: e?.status || 500,
                success: false,
            },
        )
        trackError(e instanceof Error ? e : new Error(e?.message || 'Saga error'), { saga: 'getDMLCardData', action: action?.type })
    }
}

export function* getMTECardData<P, R>(action: { type: string; payload: any; onSuccess: (response: SuccessResponse<R>) => void; onError: (response: ErrorResponse<R>) => void }): any {
    try {
        const literal: string = action.payload.literal
        const resp = yield call(CallApi.POST, APIS.EXPERIMENT.MTE_CARDS(literal), action.payload)
        if (resp.status >= 200 && resp.status < 300 && resp.data) {
            action?.onSuccess(resp.data as SuccessResponse<R>)
        } else {
            action?.onError(resp.data as ErrorResponse<R>)
        }
    } catch (e: any) {
        action?.onError(
            e?.data || {
                message: e?.message || 'Failed to fetch MTE card data',
                status_code: e?.status || 500,
                success: false,
            },
        )
    }
}

export function* getDetailAndAnalysisData<P, R>(action: {
    type: string
    payload: {
        literal: string
        group_name?: string
    }
    onSuccess: (response: SuccessResponse<R>) => void
    onError: (response: ErrorResponse<R>) => void
}): any {
    try {
        const exptLiteral: string = action.payload.literal

        const resp = yield call(CallApi.GET, APIS.EXPERIMENT.DETAIL_ANALYSIS_DATA(exptLiteral), action.payload, false)
        if (resp.status >= 200 && resp.status < 300 && resp.data) {
            action?.onSuccess(resp.data as SuccessResponse<R>)
        } else {
            console.log('/overview/details-analysis S ERROR -')
            action?.onError(resp.data as ErrorResponse<R>)
        }
    } catch (e: any) {
        // action?.onError(e.response.data)
        // ✅ CHANGED: Added null check and fallback error object
        action?.onError(
            e?.response?.data || {
                message: e?.message || 'Failed to fetch detail and analysis data',
                status_code: e?.status || 500,
                success: false,
            },
        )
        trackError(e instanceof Error ? e : new Error(e?.message || 'Saga error'), { saga: 'getDetailAndAnalysisData', action: action?.type })
    }
}

export function* getSharedUsers<P, R>(action: {
    type: string
    payload: {
        literal: string
        sort_by: string
        page: number
        per_page: number
    }
    onSuccess: (response: SuccessResponse<R>) => void
    onError: (response: ErrorResponse<R>) => void
}): any {
    try {
        const { literal, ...queryString } = action.payload
        const resp = yield call(CallApi.GET, APIS.EXPERIMENT.SHARED_USERS(literal), queryString, false)
        if (resp.status >= 200 && resp.status < 300 && resp.data) {
            action?.onSuccess(resp.data as SuccessResponse<R>)
        } else {
            action?.onError(resp.data as ErrorResponse<R>)
        }
    } catch (e: any) {
        // action?.onError(e.data)
        // ✅ CHANGED: Added null check and fallback error object
        action?.onError(
            e?.data || {
                message: e?.message || 'Failed to fetch shared users',
                status_code: e?.status || 500,
                success: false,
            },
        )
        trackError(e instanceof Error ? e : new Error(e?.message || 'Saga error'), { saga: 'getSharedUsers', action: action?.type })
    }
}

export function* postShareUsers<P, R>(action: {
    type: string
    payload: {
        literal: string
        shared_with_user_id: string[]
    }
    onSuccess: (response: SuccessResponse<R>) => void
    onError: (response: ErrorResponse<R>) => void
}): any {
    try {
        const { literal, ...param } = action.payload
        const resp = yield call(CallApi.POST, APIS.EXPERIMENT.SHARE_EXPT_TO_USERS(literal), param)
        if (resp.status >= 200 && resp.status < 300 && resp.data) {
            action?.onSuccess(resp.data as SuccessResponse<R>)
        } else {
            action?.onError(resp.data as ErrorResponse<R>)
        }
    } catch (e: any) {
        // action?.onError(e.data)
        // ✅ CHANGED: Added null check and fallback error object
        action?.onError(
            e?.data || {
                message: e?.message || 'Failed to share experiment with users',
                status_code: e?.status || 500,
                success: false,
            },
        )
        trackError(e instanceof Error ? e : new Error(e?.message || 'Saga error'), { saga: 'postShareUsers', action: action?.type })
    }
}

export function* executerct<P, R>(action: {
    type: string
    payload: {
        literal: string
    }
    onSuccess: (response: SuccessResponse<R>) => void
    onError: (response: ErrorResponse<R>) => void
}): any {
    try {
        const params: any = {}

        const resp = yield call(CallApi.GET, APIS.EXPERIMENT.EXECUTE_RCT(action.payload.literal), params, false, true)
        if (resp.status >= 200 && resp.status < 300 && resp.data) {
            action?.onSuccess(resp.data)
        } else {
            action?.onError(resp)
        }
    } catch (e: any) {
        // action?.onError(e.data)
        // ✅ CHANGED: Added null check and fallback error object
        action?.onError(
            e?.data || {
                message: e?.message || 'Failed to execute RCT',
                status_code: e?.status || 500,
                success: false,
            },
        )
        trackError(e instanceof Error ? e : new Error(e?.message || 'Saga error'), { saga: 'executerct', action: action?.type })
    }
}

export function* executerctresult<P, R>(action: {
    type: string
    payload: {
        literal: string
    }
    onSuccess: (response: SuccessResponse<R>) => void
    onError: (response: ErrorResponse<R>) => void
}): any {
    try {
        const params: any = {}
        const resp = yield call(CallApi.GET, APIS.EXPERIMENT.EXECUTE_RCT_RESULTS(action.payload.literal), params, false, true)
        if (resp.status >= 200 && resp.status < 300 && resp.data) {
            action?.onSuccess(resp.data)
        } else {
            action?.onError(resp)
        }
    } catch (e: any) {
        // action?.onError(e.data)
        // ✅ CHANGED: Added null check and fallback error object
        action?.onError(
            e?.data || {
                message: e?.message || 'Failed to fetch RCT results',
                status_code: e?.status || 500,
                success: false,
            },
        )
        trackError(e instanceof Error ? e : new Error(e?.message || 'Saga error'), { saga: 'executerctresult', action: action?.type })
    }
}

export function* getColumnsForGroupDristribution<P, R>(action: {
    type: string
    payload: {
        literal: string
        tableType: string
    }
    onSuccess: (response: SuccessResponse<R>) => void
    onError: (response: ErrorResponse<R>) => void
}): any {
    try {
        const params: any = {}

        const resp = yield call(CallApi.GET, APIS.EXPERIMENT.GET_COLUMNS_FOR_GROUP_DISTRIBUTION(action.payload.literal, action.payload.tableType), params, false, true)
        if (resp.status >= 200 && resp.status < 300 && resp.data) {
            action?.onSuccess(resp.data)
        } else {
            action?.onError(resp)
        }
    } catch (e: any) {
        // action?.onError(e.data)
        // ✅ CHANGED: Added null check and fallback error object
        action?.onError(
            e?.data || {
                message: e?.message || 'Failed to fetch columns for group distribution',
                status_code: e?.status || 500,
                success: false,
            },
        )
        trackError(e instanceof Error ? e : new Error(e?.message || 'Saga error'), { saga: 'getColumnsForGroupDristribution', action: action?.type })
    }
}

export function* getdesignkpimeasurment<P, R>(action: {
    type: string
    payload: {
        literal: string
    }
    onSuccess: (response: SuccessResponse<R>) => void
    onError: (response: ErrorResponse<R>) => void
}): any {
    try {
        const params: any = {}

        const resp = yield call(CallApi.GET, APIS.EXPERIMENT.GET_DESIGN_KPI_MEASURMENT(action.payload.literal), params, false, true)
        if (resp.status >= 200 && resp.status < 300 && resp.data) {
            action?.onSuccess(resp.data)
        } else {
            action?.onError(resp)
        }
    } catch (e: any) {
        // action?.onError(e.data)
        // ✅ CHANGED: Added null check and fallback error object
        action?.onError(
            e?.data || {
                message: e?.message || 'Failed to fetch design KPI measurement',
                status_code: e?.status || 500,
                success: false,
            },
        )
        trackError(e instanceof Error ? e : new Error(e?.message || 'Saga error'), { saga: 'getdesignkpimeasurment', action: action?.type })
    }
}

export function* executemeasurmentanalysis<P, R>(action: {
    type: string
    payload: {
        literal: string
    }
    onSuccess: (response: SuccessResponse<R>) => void
    onError: (response: ErrorResponse<R>) => void
}): any {
    try {
        const params: any = {}

        const resp = yield call(CallApi.GET, APIS.EXPERIMENT.EXECUTE_MEASURMENT_ANALYSIS(action.payload.literal), params, false, true)
        if (resp.status >= 200 && resp.status < 300 && resp.data) {
            action?.onSuccess(resp.data)
        } else {
            action?.onError(resp)
        }
    } catch (e: any) {
        // action?.onError(e.data)
        // ✅ CHANGED: Added null check and fallback error object
        action?.onError(
            e?.data || {
                message: e?.message || 'Failed to execute measurement analysis',
                status_code: e?.status || 500,
                success: false,
            },
        )
        trackError(e instanceof Error ? e : new Error(e?.message || 'Saga error'), { saga: 'executemeasurmentanalysis', action: action?.type })
    }
}

export function* executedesignmergeoverview<P, R>(action: {
    type: string
    payload: {
        literal: string
    }
    onSuccess: (response: SuccessResponse<R>) => void
    onError: (response: ErrorResponse<R>) => void
}): any {
    try {
        const params: any = {}

        const resp = yield call(CallApi.GET, APIS.EXPERIMENT.MEASURMENT_DESIGN_MERGE_OVERVIEW(action.payload.literal), params, false, true)
        if (resp.status >= 200 && resp.status < 300 && resp.data) {
            action?.onSuccess(resp.data)
        } else {
            action?.onError(resp)
        }
    } catch (e: any) {
        // action?.onError(e.data)
        // ✅ CHANGED: Added null check and fallback error object
        action?.onError(
            e?.data || {
                message: e?.message || 'Failed to fetch design merge overview',
                status_code: e?.status || 500,
                success: false,
            },
        )
        trackError(e instanceof Error ? e : new Error(e?.message || 'Saga error'), { saga: 'executedesignmergeoverview', action: action?.type })
    }
}

export function* executemtesubmit<P, R>(action: {
    type: string
    payload: {
        literal: string
    }
    onSuccess: (response: SuccessResponse<R>) => void
    onError: (response: ErrorResponse<R>) => void
}): any {
    try {
        const resp = yield call(CallApi.GET, APIS.EXPERIMENT.MTE_SUBMIT, action.payload, false, true)
        if (resp.status >= 200 && resp.status < 300 && resp.data) {
            action?.onSuccess(resp.data)
        } else {
            action?.onError(resp)
        }
    } catch (e: any) {
        console.log('Error', e)
        trackError(e instanceof Error ? e : new Error(e?.message || 'Saga error'), { saga: 'executemtesubmit', action: action?.type })
    }
}

export function* createRecurringExpt<P, R>(action: {
    type: string
    payload: {
        literal: string
        params: any
    }
    onSuccess: (response: SuccessResponse<R>) => void
    onError: (response: ErrorResponse<R>) => void
}): any {
    try {
        const { literal, params } = action.payload

        const newPayload = structuredClone(params)

        if (newPayload?.measurement_table_filters?.sql_query) {
            newPayload.measurement_table_filters.sql_query = btoa(newPayload.measurement_table_filters.sql_query)
        }

        const response = yield call(CallApi.POST, APIS.EXPERIMENT.RECURRING_EXPERIMENT_CREATE(literal), newPayload, true)
        if (response.status >= 200 && response.status < 300 && response.data) {
            action?.onSuccess(response.data)
        } else {
            action?.onError(response)
        }
    } catch (e: any) {
        // action?.onError(e)
        // ✅ CHANGED: Added null check and fallback error object
        action?.onError(
            e?.data || {
                message: e?.message || 'Failed to create recurring experiment',
                status_code: e?.status || 500,
                success: false,
            },
        )
        trackError(e instanceof Error ? e : new Error(e?.message || 'Saga error'), { saga: 'createRecurringExpt', action: action?.type })
    }
}

export function* updateRecurringExpt<P, R>(action: {
    type: string
    payload: {
        literal: string
        params: any
    }
    onSuccess: (response: SuccessResponse<R>) => void
    onError: (response: ErrorResponse<R>) => void
}): any {
    try {
        const { literal, params } = action.payload

        const response = yield call(CallApi.POST, APIS.EXPERIMENT.RECURRING_EXPERIMENT_UPDATE(literal), params, true)
        if (response.status >= 200 && response.status < 300 && response.data) {
            action?.onSuccess(response.data)
        } else {
            action?.onError(response)
        }
    } catch (e: any) {
        // action?.onError(e)
        // ✅ CHANGED: Added null check and fallback error object
        action?.onError(
            e?.data || {
                message: e?.message || 'Failed to update recurring experiment',
                status_code: e?.status || 500,
                success: false,
            },
        )
        trackError(e instanceof Error ? e : new Error(e?.message || 'Saga error'), { saga: 'updateRecurringExpt', action: action?.type })
    }
}

export function* recurringUpdateDesignTable<P, R>(action: {
    type: string
    payload: {
        literal: string
        params: any
    }
    onSuccess: (response: SuccessResponse<R>) => void
    onError: (response: ErrorResponse<R>) => void
}): any {
    try {
        const { literal, params } = action.payload

        const response = yield call(CallApi.POST, APIS.EXPERIMENT.RECURRING_UPDATE_DESIGN_TABLE(literal), params, true)
        if (response.status >= 200 && response.status < 300 && response.data) {
            action?.onSuccess(response.data)
        } else {
            action?.onError(response)
        }
    } catch (e: any) {
        // action?.onError(e)
        // ✅ CHANGED: Added null check and fallback error object
        action?.onError(
            e?.data || {
                message: e?.message || 'Failed to update design table for recurring experiment',
                status_code: e?.status || 500,
                success: false,
            },
        )
        trackError(e instanceof Error ? e : new Error(e?.message || 'Saga error'), { saga: 'recurringUpdateDesignTable', action: action?.type })
    }
}

export function* recurringGetTableColumns<P, R>(action: {
    type: string
    payload: {
        literal: string
        params: any
    }
    onSuccess: (response: SuccessResponse<R>) => void
    onError: (response: ErrorResponse<R>) => void
}): any {
    try {
        const { literal, params } = action.payload

        const response = yield call(CallApi.POST, APIS.EXPERIMENT.RECURRING_GET_DESIGN_TABLE_COLUMNS(literal), params, true)
        if (response.status >= 200 && response.status < 300 && response.data) {
            action?.onSuccess(response.data)
        } else {
            action?.onError(response)
        }
    } catch (e: any) {
        //  action?.onError(e)
        // ✅ CHANGED: Added null check and fallback error object
        action?.onError(
            e?.data || {
                message: e?.message || 'Failed to fetch design table columns for recurring experiment',
                status_code: e?.status || 500,
                success: false,
            },
        )
        trackError(e instanceof Error ? e : new Error(e?.message || 'Saga error'), { saga: 'recurringGetTableColumns', action: action?.type })
    }
}

export function* recurringGetDesignPreviewData<P, R>(action: {
    type: string
    payload: {
        literal: string
        params: any
    }
    onSuccess: (response: SuccessResponse<R>) => void
    onError: (response: ErrorResponse<R>) => void
}): any {
    try {
        const { literal, params } = action.payload

        const response = yield call(CallApi.POST, APIS.EXPERIMENT.RECURRING_GET_DESIGN_PREVIEW_DATA(literal), params, true)
        if (response.status >= 200 && response.status < 300 && response.data) {
            action?.onSuccess(response.data)
        } else {
            action?.onError(response)
        }
    } catch (e: any) {
        // action?.onError(e)
        // ✅ CHANGED: Added null check and fallback error object
        action?.onError(
            e?.data || {
                message: e?.message || 'Failed to fetch design preview data for recurring experiment',
                status_code: e?.status || 500,
                success: false,
            },
        )
        trackError(e instanceof Error ? e : new Error(e?.message || 'Saga error'), { saga: 'recurringGetDesignPreviewData', action: action?.type })
    }
}

export function* recurringUpdateConfig<P, R>(action: {
    type: string
    payload: {
        literal: string
        params: any
    }
    onSuccess: (response: SuccessResponse<R>) => void
    onError: (response: ErrorResponse<R>) => void
}): any {
    try {
        const { literal, params } = action.payload

        const response = yield call(CallApi.POST, APIS.EXPERIMENT.RECURRING_UPDATE_DESIGN_CONFIG(literal), params, true)
        if (response.status >= 200 && response.status < 300 && response.data) {
            action?.onSuccess(response.data)
        } else {
            action?.onError(response)
        }
    } catch (e: any) {
        // action?.onError(e)
        // ✅ CHANGED: Added null check and fallback error object
        action?.onError(
            e?.data || {
                message: e?.message || 'Failed to update design config for recurring experiment',
                status_code: e?.status || 500,
                success: false,
            },
        )
        trackError(e instanceof Error ? e : new Error(e?.message || 'Saga error'), { saga: 'recurringUpdateConfig', action: action?.type })
    }
}

export function* playPauseExpt<P, R>(action: {
    type: string
    payload: {
        literal: string
        status: string
    }
    onSuccess: (response: SuccessResponse<R>) => void
    onError: (response: ErrorResponse<R>) => void
}): any {
    try {
        const { literal, status } = action.payload

        const response = yield call(CallApi.POST, APIS.EXPERIMENT.PLAY_OR_PAUSE(literal, status), {}, false)
        if (response.status >= 200 && response.status < 300 && response.data) {
            action?.onSuccess(response.data)
        } else {
            action?.onError(response)
        }
    } catch (e: any) {
        // action?.onError(e)
        // ✅ CHANGED: Added null check and fallback error object
        action?.onError(
            e?.data || {
                message: e?.message || 'Failed to play or pause experiment',
                status_code: e?.status || 500,
                success: false,
            },
        )
        trackError(e instanceof Error ? e : new Error(e?.message || 'Saga error'), { saga: 'playPauseExpt', action: action?.type })
    }
}

export function* sqlPreivewData<P, R>(action: {
    type: string
    payload: {
        [name: string]: any
    }
    onSuccess: (response: SuccessResponse<R>) => void
    onError: (response: ErrorResponse<R>) => void
}): any {
    try {
        const page = action.payload.page
        const per_page = action.payload.per_page

        const urlParams: any = { page, per_page }

        const data: any = action.payload

        delete data.page
        delete data.per_page

        const resp = yield call(CallApi.POST, APIS.EXPERIMENT.SQL_PREVIEW_DATA + '?' + serialize(urlParams), data, true)
        if (resp.status >= 200 && resp.status < 300 && resp.data) {
            action?.onSuccess(resp.data as SuccessResponse<R>)
        } else {
            action?.onError(resp.data as ErrorResponse<R>)
        }
    } catch (e: any) {
        // action?.onError(e.response.data.message as ErrorResponse<R>)
        // ✅ CHANGED: Added null check and fallback error object
        action?.onError(
            e?.response?.data?.message || {
                message: e?.message || 'Failed to fetch SQL preview data',
                status_code: e?.status || 500,
                success: false,
            },
        )
        trackError(e instanceof Error ? e : new Error(e?.message || 'Saga error'), { saga: 'sqlPreivewData', action: action?.type })
    }
}

export function* GetMeasurementGroupDistributionData<P, R>(action: {
    type: string
    payload: {
        literal: string
    }
    onSuccess: (response: SuccessResponse<R>) => void
    onError: (response: ErrorResponse<R>) => void
}): any {
    try {
        const response = yield call(CallApi.GET, APIS.EXPERIMENT.MEASUREMENT_GROUP_DISTRIBUTION_DATA, action.payload, false, true)
        if (response.status >= 200 && response.status < 300 && response.data) {
            action?.onSuccess(response.data)
        } else {
            action?.onError(response)
        }
    } catch (e: any) {
        // action?.onError(e)
        // ✅ CHANGED: Added null check and fallback error object
        action?.onError(
            e?.data || {
                message: e?.message || 'Failed to fetch measurement group distribution data',
                status_code: e?.status || 500,
                success: false,
            },
        )
        trackError(e instanceof Error ? e : new Error(e?.message || 'Saga error'), { saga: 'GetMeasurementGroupDistributionData', action: action?.type })
    }
}
export function* migrateMeasurementDataToCsv<P, R>(action: {
    type: string
    payload: {
        literal: string
    }
    onSuccess: (response: SuccessResponse<R>) => void
    onError: (response: ErrorResponse<R>) => void
}): any {
    try {
        const response = yield call(CallApi.GET, APIS.EXPERIMENT.MIGRATE_MEASUREMENT_DATA_TO_CSV(action.payload.literal), action.payload, false, true)
        if (response.status >= 200 && response.status < 300 && response.data) {
            action?.onSuccess(response.data)
        } else {
            action?.onError(response)
        }
    } catch (e: any) {
        // action?.onError(e)
        // ✅ CHANGED: Added null check and fallback error object
        action?.onError(
            e?.data || {
                message: e?.message || 'Failed to migrate measurement data to CSV',
                status_code: e?.status || 500,
                success: false,
            },
        )
        trackError(e instanceof Error ? e : new Error(e?.message || 'Saga error'), { saga: 'migrateMeasurementDataToCsv', action: action?.type })
    }
}

export function* downloadRctGroupResultS<P, R>(action: {
    type: string
    payload: {
        literal: string
        [name: string]: any
    }
    onSuccess: (response: SuccessResponse<R>) => void
    onError: (response: ErrorResponse<R>) => void
}): any {
    try {
        const literal: string = action?.payload?.literal
        const data: any = action.payload?.formData
        const resp = yield call(CallApi.GET, APIS.EXPERIMENT.DOWNLOAD_RCT_GROUP_RESULTS(literal), data, false, true)

        if (resp.status >= 200 && resp.status < 300 && resp.data) {
            action?.onSuccess(resp.data as SuccessResponse<R>)
        } else {
            action?.onError(resp.data as ErrorResponse<R>)
        }
    } catch (e: any) {
        // action?.onError(e.data)
        // ✅ CHANGED: Added null check and fallback error object
        action?.onError(
            e?.data || {
                message: e?.message || 'Failed to download RCT group results',
                status_code: e?.status || 500,
                success: false,
            },
        )
        trackError(e instanceof Error ? e : new Error(e?.message || 'Saga error'), { saga: 'downloadRctGroupResultS', action: action?.type })
    }
}

export function* GetDesignGroupDistributionData<P, R>(action: {
    type: string
    payload: {
        literal: string
    }
    onSuccess: (response: SuccessResponse<R>) => void
    onError: (response: ErrorResponse<R>) => void
}): any {
    try {
        const response = yield call(CallApi.GET, APIS.EXPERIMENT.DESIGN_GROUP_DISTRIBUTION_DETAILS, action.payload, false, true)
        if (response.status >= 200 && response.status < 300 && response.data) {
            action?.onSuccess(response.data)
        } else {
            action?.onError(response)
        }
    } catch (e: any) {
        // action?.onError(e)
        // ✅ CHANGED: Added null check and fallback error object
        action?.onError(
            e?.data || {
                message: e?.message || 'Failed to fetch design group distribution data',
                status_code: e?.status || 500,
                success: false,
            },
        )
        trackError(e instanceof Error ? e : new Error(e?.message || 'Saga error'), { saga: 'GetDesignGroupDistributionData', action: action?.type })
    }
}

export function* Getkpichartdatabydate<P, R>(action: {
    type: string
    payload: {
        literal: string
        [name: string]: any
    }
    onSuccess: (response: SuccessResponse<R>) => void
    onError: (response: ErrorResponse<R>) => void
}): any {
    try {
        const literal: string = action?.payload?.literal
        const data: any = action.payload?.formData
        const resp = yield call(CallApi.GET, APIS.EXPERIMENT.GET_KPI_CHART_DATA_BY_DATE(literal), data, false, true)

        if (resp.status >= 200 && resp.status < 300 && resp.data) {
            action?.onSuccess(resp.data as SuccessResponse<R>)
        } else {
            action?.onError(resp.data as ErrorResponse<R>)
        }
    } catch (e: any) {
        // action?.onError(e.data)
        // ✅ CHANGED: Added null check and fallback error object
        action?.onError(
            e?.data || {
                message: e?.message || 'Failed to fetch KPI chart data by date',
                status_code: e?.status || 500,
                success: false,
            },
        )
        trackError(e instanceof Error ? e : new Error(e?.message || 'Saga error'), { saga: 'Getkpichartdatabydate', action: action?.type })
    }
}

export function* Getobsfeaturedistributionbarchart<P, R>(action: {
    type: string
    payload: {
        literal: string
        [name: string]: any
    }
    onSuccess: (response: SuccessResponse<R>) => void
    onError: (response: ErrorResponse<R>) => void
}): any {
    try {
        const literal: string = action?.payload?.literal
        const data: any = action.payload?.formData
        const resp = yield call(CallApi.GET, APIS.EXPERIMENT.GET_OBS_FEATURE_DISTRIBUTION_BAR_CHART(literal), data, false, true)

        if (resp.status >= 200 && resp.status < 300 && resp.data) {
            action?.onSuccess(resp.data as SuccessResponse<R>)
        } else {
            action?.onError(resp.data as ErrorResponse<R>)
        }
    } catch (e: any) {
        // action?.onError(e.data)
        // ✅ CHANGED: Added null check and fallback error object
        action?.onError(
            e?.data || {
                message: e?.message || 'Failed to fetch observation feature distribution bar chart',
                status_code: e?.status || 500,
                success: false,
            },
        )
        trackError(e instanceof Error ? e : new Error(e?.message || 'Saga error'), { saga: 'Getobsfeaturedistributionbarchart', action: action?.type })
    }
}

export function* Uploadexperimentfiles<P, R>(action: {
    type: string
    payload: {
        filetype: string
        [name: string]: any
    }
    onSuccess: (response: SuccessResponse<R>) => void
    onError: (response: ErrorResponse<R>) => void
}): any {
    try {
        const filetype: string = action?.payload?.filetype
        const data: any = action.payload?.formData

        const resp = yield call(CallApi.POST, APIS.EXPERIMENT_FILES.UPLOAD_FILE + '?type=' + filetype, data, true, (progrss: any) => {
            if (action?.payload?.progress) {
                action?.payload?.progress(progrss)
            }
        })

        if (resp.status >= 200 && resp.status < 300 && resp.data) {
            action?.onSuccess(resp.data as SuccessResponse<R>)
        } else {
            action?.onError(resp.data as ErrorResponse<R>)
        }
    } catch (e: any) {
        // action?.onError(e.data)
        // ✅ CHANGED: Added null check and fallback error object
        action?.onError(
            e?.data || {
                message: e?.message || 'Failed to upload experiment files',
                status_code: e?.status || 500,
                success: false,
            },
        )
        trackError(e instanceof Error ? e : new Error(e?.message || 'Saga error'), { saga: 'Uploadexperimentfiles', action: action?.type })
    }
}

export function* Mapexperimentfile<P, R>(action: {
    type: string
    payload: {
        literal: string
        [name: string]: any
    }
    onSuccess: (response: SuccessResponse<R>) => void
    onError: (response: ErrorResponse<R>) => void
}): any {
    try {
        const literal: string = action?.payload?.literal
        const data: any = action.payload?.formData
        const resp = yield call(CallApi.POST, APIS.EXPERIMENT_FILES.MAP_EXPERIMENT_FILES + 'experiment_literal=' + literal, data, true)
        if (resp.status >= 200 && resp.status < 300 && resp.data) {
            action?.onSuccess(resp.data as SuccessResponse<R>)
        } else {
            action?.onError(resp.data as ErrorResponse<R>)
        }
    } catch (e: any) {
        // action?.onError(e.data)
        // ✅ CHANGED: Added null check and fallback error object
        action?.onError(
            e?.data || {
                message: e?.message || 'Failed to map experiment file',
                status_code: e?.status || 500,
                success: false,
            },
        )
        trackError(e instanceof Error ? e : new Error(e?.message || 'Saga error'), { saga: 'Mapexperimentfile', action: action?.type })
    }
}

export function* Deleteexperimentfile<P, R>(action: {
    type: string
    payload: {
        literal: string
        file_id: number
    }
    onSuccess: (response: SuccessResponse<R>) => void
    onError: (response: ErrorResponse<R>) => void
}): any {
    try {
        const resp = yield call(CallApi.DELETE, APIS.EXPERIMENT_FILES.DELETE_FILES(action.payload.literal, action.payload.file_id), {}, true)
        if (resp.status >= 200 && resp.status < 300 && resp.data) {
            action?.onSuccess(resp.data as SuccessResponse<R>)
        } else {
            action?.onError(resp.data as ErrorResponse<R>)
        }
    } catch (e: any) {
        // action?.onError(e.data)
        // ✅ CHANGED: Added null check and fallback error object
        action?.onError(
            e?.data || {
                message: e?.message || 'Failed to delete experiment file',
                status_code: e?.status || 500,
                success: false,
            },
        )
        trackError(e instanceof Error ? e : new Error(e?.message || 'Saga error'), { saga: 'Deleteexperimentfile', action: action?.type })
    }
}

export function* Downloadexperimentfile<P, R>(action: {
    type: string
    payload: {
        file_id: number
        [name: string]: any
    }
    onSuccess: (response: SuccessResponse<R>) => void
    onError: (response: ErrorResponse<R>) => void
}): any {
    try {
        const file_id: number = action?.payload?.file_id

        const data: any = action.payload?.formData
        const resp = yield call(CallApi.GET, APIS.EXPERIMENT_FILES.DOWNLOAD_FILES(file_id), data, false, true)

        if (resp.status >= 200 && resp.status < 300 && resp.data) {
            action?.onSuccess(resp.data as SuccessResponse<R>)
        } else {
            action?.onError(resp.data as ErrorResponse<R>)
        }
    } catch (e: any) {
        // action?.onError(e.data)
        // ✅ CHANGED: Added null check and fallback error object
        action?.onError(
            e?.data || {
                message: e?.message || 'Failed to download experiment file',
                status_code: e?.status || 500,
                success: false,
            },
        )
        trackError(e instanceof Error ? e : new Error(e?.message || 'Saga error'), { saga: 'Downloadexperimentfile', action: action?.type })
    }
}
export function* SampleSizeCalculate<P, R>(action: { type: string; payload: any; onSuccess: (response: SuccessResponse<R>) => void; onError: (response: ErrorResponse<R>) => void }): any {
    try {
        const data: any = action.payload
        const resp = yield call(CallApi.POST, APIS.EXPERIMENT.SAMPLE_SIZE_CALCULATE, data, true)

        if (resp.status >= 200 && resp.status < 300 && resp.data) {
            action?.onSuccess(resp.data as SuccessResponse<R>)
        } else {
            action?.onError(resp.data as ErrorResponse<R>)
        }
    } catch (e: any) {
        // action?.onError(e?.response?.data.message ? e.response.data : e.data)
        // ✅ CHANGED: Added null check and fallback error object
        action?.onError(
            e?.data || {
                message: e?.message || 'Failed to calculate sample size',
                status_code: e?.status || 500,
                success: false,
            },
        )
        trackError(e instanceof Error ? e : new Error(e?.message || 'Saga error'), { saga: 'SampleSizeCalculate', action: action?.type })
    }
}

export function* getAvgMetricDesign<P, R>(action: {
    type: string
    payload: {
        literal: string
    }
    onSuccess: (response: SuccessResponse<R>) => void
    onError: (response: ErrorResponse<R>) => void
}): any {
    try {
        const { literal } = action.payload

        const resp = yield call(CallApi.GET, APIS.EXPERIMENT.AVG_METRIC_DESIGN(literal), action.payload, false)
        if (resp.status >= 200 && resp.status < 300 && resp.data) {
            action?.onSuccess(resp.data as SuccessResponse<R>)
        } else {
            action?.onError(resp.data as ErrorResponse<R>)
        }
    } catch (e: any) {
        // action?.onError(e.data)
        // ✅ CHANGED: Added null check and fallback error object
        action?.onError(
            e?.data || {
                message: e?.message || 'Failed to get average metric design',
                status_code: e?.status || 500,
                success: false,
            },
        )
        trackError(e instanceof Error ? e : new Error(e?.message || 'Saga error'), { saga: 'getAvgMetricDesign', action: action?.type })
    }
}

export function* getAdditionalTableData<P, R>(action: {
    type: string
    payload: {
        literal: string
    }
    onSuccess: (response: SuccessResponse<R>) => void
    onError: (response: ErrorResponse<R>) => void
}): any {
    try {
        const literal = action.payload?.literal
        const resp = yield call(CallApi.POST, APIS.EXPERIMENT.ADDITIONAL_TABLE_DATA(literal), action.payload, false, true)
        if (resp.status >= 200 && resp.status < 300 && resp.data) {
            action?.onSuccess(resp.data as SuccessResponse<R>)
        } else {
            action?.onError(resp.data as ErrorResponse<R>)
        }
    } catch (e: any) {
        // action?.onError(e?.response?.data?.message)
        // ✅ CHANGED: Added null check and fallback error object
        action?.onError(
            e?.data || {
                message: e?.message || 'Failed to get additional table data',
                status_code: e?.status || 500,
                success: false,
            },
        )
        trackError(e instanceof Error ? e : new Error(e?.message || 'Saga error'), { saga: 'getAdditionalTableData', action: action?.type })
    }
}

export function* uploadExperimentAdditionfile<P, R>(action: {
    type: string
    payload: {
        literal: string
        [name: string]: any
    }
    onSuccess: (response: SuccessResponse<R>) => void
    onError: (response: ErrorResponse<R>) => void
}): any {
    try {
        const literal: string = action?.payload?.literal
        const data: any = action.payload?.formData

        const resp = yield call(CallApi.POST, APIS.EXPERIMENT.ADDITIONAL_FILE_UPLOAD(literal), data, true, (progrss: any) => {
            if (action?.payload?.progress) {
                action?.payload?.progress(progrss)
            }
        })

        if (resp.status >= 200 && resp.status < 300 && resp.data) {
            action?.onSuccess(resp.data as SuccessResponse<R>)
        } else {
            action?.onError(resp.data as ErrorResponse<R>)
        }
    } catch (e: any) {
        // action?.onError(e.data)
        // ✅ CHANGED: Added null check and fallback error object
        action?.onError(
            e?.data || {
                message: e?.message || 'Failed to upload experiment additional file',
                status_code: e?.status || 500,
                success: false,
            },
        )
        trackError(e instanceof Error ? e : new Error(e?.message || 'Saga error'), { saga: 'uploadExperimentAdditionfile', action: action?.type })
    }
}

export function* getComments<P, R>(action: {
    type: string
    payload: {
        experiment_literal: string
    }
    onSuccess: (response: SuccessResponse<R>) => void
    onError: (response: ErrorResponse<R>) => void
}): any {
    try {
        const literal: string = action.payload.experiment_literal
        //const data: any = action.payload

        const resp = yield call(CallApi.GET, APIS.EXPERIMENT.COMMENTS(literal), {}, false, true)

        if (resp.status >= 200 && resp.status < 300 && resp.data) {
            action?.onSuccess(resp.data as SuccessResponse<R>)
        } else {
            action?.onError(resp.data as ErrorResponse<R>)
        }
    } catch (e: any) {
        // action?.onError(e.data)
        // ✅ CHANGED: Added null check and fallback error object
        action?.onError(
            e?.data || {
                message: e?.message || 'Failed to get comments',
                status_code: e?.status || 500,
                success: false,
            },
        )
        trackError(e instanceof Error ? e : new Error(e?.message || 'Saga error'), { saga: 'getComments', action: action?.type })
    }
}
export function* postComment<P, R>(action: {
    type: string
    payload: {
        experiment_literal: string
        post: any
    }
    onSuccess: (response: SuccessResponse<R>) => void
    onError: (response: ErrorResponse<R>) => void
}): any {
    try {
        const literal: string = action.payload.experiment_literal
        const data: any = action.payload.post

        const resp = yield call(CallApi.POST, APIS.EXPERIMENT.COMMENTS(literal), data, true)

        if (resp.status >= 200 && resp.status < 300 && resp.data) {
            action?.onSuccess(resp.data as SuccessResponse<R>)
        } else {
            action?.onError(resp.data as ErrorResponse<R>)
        }
    } catch (e: any) {
        // action?.onError(e.data)
        // ✅ CHANGED: Added null check and fallback error object
        action?.onError(
            e?.data || {
                message: e?.message || 'Failed to post comment',
                status_code: e?.status || 500,
                success: false,
            },
        )
        trackError(e instanceof Error ? e : new Error(e?.message || 'Saga error'), { saga: 'postComment', action: action?.type })
    }
}

export function* getInconclusiveReason<P, R>(action: {
    type: string
    payload: {
        literal: string
    }
    onSuccess: (response: SuccessResponse<R>) => void
    onError: (response: ErrorResponse<R>) => void
}): any {
    try {
        const { literal, ...payload } = action.payload
        const resp = yield call(CallApi.GET, APIS.EXPERIMENT.GET_INCONCLUSIVE_REASON(literal), payload, false, true)
        if (resp.status >= 200 && resp.status < 300 && resp.data) {
            action?.onSuccess(resp.data as SuccessResponse<R>)
        } else {
            action?.onError(resp.data as ErrorResponse<R>)
        }
    } catch (e: any) {
        // action?.onError(e.data)
        // ✅ CHANGED: Added null check and fallback error object
        action?.onError(
            e?.data || {
                message: e?.message || 'Failed to get inconclusive reason',
                status_code: e?.status || 500,
                success: false,
            },
        )
        trackError(e instanceof Error ? e : new Error(e?.message || 'Saga error'), { saga: 'getInconclusiveReason', action: action?.type })
    }
}

export function* downloadExperimentlog<P, R>(action: {
    type: string
    payload: {
        literal: string
        [name: string]: any
    }
    onSuccess: (response: SuccessResponse<R>) => void
    onError: (response: ErrorResponse<R>) => void
}): any {
    try {
        const literal: string = action?.payload?.literal
        const data: any = action.payload?.formData

        const resp = yield call(CallApi.GETDOWNLOAD, APIS.EXPERIMENT.DOWNLOAD_LOG(literal) + '?type=csv', data, true, true)

        if (resp.status >= 200 && resp.status < 300 && resp.data) {
            action?.onSuccess(resp.data as SuccessResponse<R>)
        } else {
            action?.onError(resp.data as ErrorResponse<R>)
        }
    } catch (e: any) {
        // action?.onError(e.data)
        // ✅ CHANGED: Added null check and fallback error object
        action?.onError(
            e?.data || {
                message: e?.message || 'Failed to download experiment log',
                status_code: e?.status || 500,
                success: false,
            },
        )
        trackError(e instanceof Error ? e : new Error(e?.message || 'Saga error'), { saga: 'downloadExperimentlog', action: action?.type })
    }
}

export function* makePartial<P, R>(action: {
    type: string
    payload: {
        literal: string
        payload: {
            experiment_name: string
            start_date: string
            end_date: string
        }
    }
    onSuccess: (response: SuccessResponse<R>) => void
    onError: (response: ErrorResponse<R>) => void
}): any {
    try {
        const literal: string = action.payload.literal

        const resp = yield call(CallApi.POST, APIS.EXPERIMENT.MAKE_PARTIAL(literal), action.payload.payload, true)
        if (resp.status >= 200 && resp.status < 300 && resp.data) {
            action?.onSuccess(resp.data as SuccessResponse<R>)
        } else {
            action?.onError(resp as ErrorResponse<R>)
        }
    } catch (e: any) {
        // action?.onError(e.response.data)
        // ✅ CHANGED: Added null check and fallback error object
        action?.onError(
            e?.response?.data || {
                message: e?.message || 'Failed to make partial',
                status_code: e?.status || 500,
                success: false,
            },
        )
        trackError(e instanceof Error ? e : new Error(e?.message || 'Saga error'), { saga: 'makePartial', action: action?.type })
    }
}

export function* deleteInsights<P, R>(action: {
    type: string
    payload: {
        literal: string
    }
    onSuccess: (response: SuccessResponse<R>) => void
    onError: (response: ErrorResponse<R>) => void
}): any {
    try {
        const literal: string = action.payload.literal

        const resp = yield call(CallApi.DELETE, APIS.EXPERIMENT.DELETE_INSIGHTS(literal), {}, true)
        if (resp.status >= 200 && resp.status < 300 && resp.data) {
            action?.onSuccess(resp.data as SuccessResponse<R>)
        } else {
            action?.onError(resp as ErrorResponse<R>)
        }
    } catch (e: any) {
        // action?.onError(e.response.data)
        // ✅ CHANGED: Added null check and fallback error object
        action?.onError(
            e?.response?.data || {
                message: e?.message || 'Failed to delete insights',
                status_code: e?.status || 500,
                success: false,
            },
        )
        trackError(e instanceof Error ? e : new Error(e?.message || 'Saga error'), { saga: 'deleteInsights', action: action?.type })
    }
}

export function* experimentDropDown<p, R>(action: {
    type: string
    payload: {
        column_name: string
        Page?: number
        per_page?: number
        paginated?: boolean
        filter_value?: string
        include_is_partial?: boolean
        from_top_learning?: boolean
        my_scope?: boolean
        selectedFilters: any
    }
    onSuccess: (response: SuccessResponse<R>) => void
    onError: (response: ErrorResponse<R>) => void
}): any {
    try {
        const columnKeyMap: any = {
            country: 'country_ids',
            use_case: 'use_case_ids',
            experiment_owner: 'created_by_ids',
        }

        const updatedSelectedFilters = { ...action.payload.selectedFilters }
        const deleteParamName = columnKeyMap[action.payload.column_name] || action.payload.column_name
        delete updatedSelectedFilters[deleteParamName]

        // Create a new payload object to avoid mutating the original
        const updatedPayload = {
            ...action.payload,
            selectedFilters: updatedSelectedFilters,
        }
        const resp = yield call(CallApi.POST, APIS.EXPERIMENT.EXPERIMENT_DROPDOWN(updatedPayload), updatedPayload.selectedFilters, true)
        if (resp.status >= 200 && resp.status < 300 && resp.data) {
            action?.onSuccess(resp.data as SuccessResponse<R>)
        } else {
            action?.onError(resp.data as ErrorResponse<R>)
        }
    } catch (e: any) {
        // action?.onError(e.data)
        // ✅ CHANGED: Added null check and fallback error object
        action?.onError(
            e?.data || {
                message: e?.message || 'Failed to get experiment dropdown',
                status_code: e?.status || 500,
                success: false,
            },
        )
        trackError(e instanceof Error ? e : new Error(e?.message || 'Saga error'), { saga: 'experimentDropDown', action: action?.type })
    }
}

export function* updateUnitAPI<p, R>(action: {
    type: string
    payload: {
        kpi_unit: string
        literral_value: string
        has_value: boolean
        literal: string
    }
    onSuccess: (response: SuccessResponse<R>) => void
    onError: (response: ErrorResponse<R>) => void
}): any {
    try {
        const { literal, ...payload } = action.payload
        const resp = yield call(CallApi.POST, APIS.EXPERIMENT.UPDATE_KPI_UNIT(literal), payload, true)
        if (resp.status >= 200 && resp.status < 300 && resp.data) {
            action?.onSuccess(resp.data as SuccessResponse<R>)
        } else {
            action?.onError(resp.data as ErrorResponse<R>)
        }
    } catch (e: any) {
        // action?.onError(e.data)
        // ✅ CHANGED: Added null check and fallback error object
        action?.onError(
            e?.data || {
                message: e?.message || 'Failed to update KPI unit',
                status_code: e?.status || 500,
                success: false,
            },
        )
        trackError(e instanceof Error ? e : new Error(e?.message || 'Saga error'), { saga: 'updateUnitAPI', action: action?.type })
    }
}
export function* generateDataQualityReport<p, R>(action: {
    type: string
    payload: {
        literal: string
    }
    onSuccess: (response: SuccessResponse<R>) => void
    onError: (response: ErrorResponse<R>) => void
}): any {
    try {
        const { literal, ...payload } = action.payload
        const newPayload: any = structuredClone(payload)

        if (newPayload?.query) {
            newPayload.query = btoa(newPayload.query)
        }

        const resp = yield call(CallApi.POST, APIS.EXPERIMENT.GENERATE_DATA_QUALITY_REPORT(literal), newPayload, true)
        if (resp.status >= 200 && resp.status < 300 && resp.data) {
            action?.onSuccess(resp.data as SuccessResponse<R>)
        } else {
            action?.onError(resp.data as ErrorResponse<R>)
        }
    } catch (e: any) {
        // action?.onError(e.data)
        // ✅ CHANGED: Added null check and fallback error object
        action?.onError(
            e?.data || {
                message: e?.message || 'Failed to generate data quality report',
                status_code: e?.status || 500,
                success: false,
            },
        )
        trackError(e instanceof Error ? e : new Error(e?.message || 'Saga error'), { saga: 'generateDataQualityReport', action: action?.type })
    }
}
export function* markasBuPriority<P, R>(action: {
    type: string
    payload: {
        literal: string
        is_bu_prioritize: boolean
    }
    onSuccess: (response: SuccessResponse<R>) => void
    onError: (response: ErrorResponse<R>) => void
}): any {
    try {
        const { literal, ...payload } = action.payload
        const resp = yield call(CallApi.POST, APIS.EXPERIMENT.MARK_AS_BU_PRIORITY(literal) + '?is_bu_prioritize=' + payload.is_bu_prioritize, payload, true)
        if (resp.status >= 200 && resp.status < 300 && resp.data) {
            action?.onSuccess(resp.data as SuccessResponse<R>)
        } else {
            action?.onError(resp.data as ErrorResponse<R>)
        }
    } catch (e: any) {
        // action?.onError(e.data)
        // ✅ CHANGED: Added null check and fallback error object
        action?.onError(
            e?.data || {
                message: e?.message || 'Failed to mark as BU priority',
                status_code: e?.status || 500,
                success: false,
            },
        )
        trackError(e instanceof Error ? e : new Error(e?.message || 'Saga error'), { saga: 'markasBuPriority', action: action?.type })
    }
}

export function* shareableUserList<P, R>(action: {
    type: string
    payload: {
        literal: string
    }
    onSuccess: (response: SuccessResponse<R>) => void
    onError: (response: ErrorResponse<R>) => void
}): any {
    try {
        const literal: string = action.payload.literal
        const resp = yield call(CallApi.GET, APIS.EXPERIMENT.SHARABLE_USER_LIST(literal), {}, true)
        if (resp.status >= 200 && resp.status < 300 && resp.data) {
            action?.onSuccess(resp.data as SuccessResponse<R>)
        } else {
            action?.onError(resp.data as ErrorResponse<R>)
        }
    } catch (e: any) {
        action?.onError(e.data)
    }
}

export function* experimentsSharedWidthMe<P, R>(action: { type: string; payload: any; onSuccess: (response: SuccessResponse<R>) => void; onError: (response: ErrorResponse<R>) => void }): any {
    try {
        const resp = yield call(CallApi.POST, APIS.EXPERIMENT.EXPERIMENTS_SHARED_WITH_ME, action.payload, true)
        if (resp.status >= 200 && resp.status < 300 && resp.data) {
            action?.onSuccess(resp.data as SuccessResponse<R>)
        } else {
            action?.onError(resp.data as ErrorResponse<R>)
        }
    } catch (e: any) {
        action?.onError(e.data)
    }
}
export function* getRecurringTableDataPreview<P, R>(action: { type: string; payload: any; onSuccess: (response: SuccessResponse<R>) => void; onError: (response: ErrorResponse<R>) => void }): any {
    try {
        const { literal, ...payload } = action.payload
        const newPayload = structuredClone(payload)

        if (newPayload?.measurement_table_filters?.sql_query) {
            newPayload.measurement_table_filters.sql_query = btoa(newPayload.measurement_table_filters.sql_query)
        }

        const resp = yield call(CallApi.POST, APIS.EXPERIMENT.GET_RECURRING_TABLE_DATA_PREVIEW(literal), newPayload, true)
        if (resp.status >= 200 && resp.status < 300 && resp.data) {
            action?.onSuccess(resp.data as SuccessResponse<R>)
        } else {
            action?.onError(resp.data as ErrorResponse<R>)
        }
    } catch (e: any) {
        action?.onError(e.data)
    }
}
export function* getRecurringColumnsData<P, R>(action: { type: string; payload: any; onSuccess: (response: SuccessResponse<R>) => void; onError: (response: ErrorResponse<R>) => void }): any {
    try {
        const { literal, ...payload } = action.payload
        const newPayload = structuredClone(payload)

        if (newPayload?.measurement_table_filters?.sql_query) {
            newPayload.measurement_table_filters.sql_query = btoa(newPayload.measurement_table_filters.sql_query)
        }
        const resp = yield call(CallApi.POST, APIS.EXPERIMENT.GET_RECURRING_COLUMNS_DATA(literal), newPayload, true)
        if (resp.status >= 200 && resp.status < 300 && resp.data) {
            action?.onSuccess(resp.data as SuccessResponse<R>)
        } else {
            action?.onError(resp.data as ErrorResponse<R>)
        }
    } catch (e: any) {
        action?.onError(e.data)
    }
}
export function* getRecurringRequiredColumnList<P, R>(action: { type: string; payload: any; onSuccess: (response: SuccessResponse<R>) => void; onError: (response: ErrorResponse<R>) => void }): any {
    try {
        const { literal, ...payload } = action.payload
        const resp = yield call(CallApi.GET, APIS.EXPERIMENT.GET_RECURRING_REQUIRED_COLUMN_LIST(literal), payload, false, true)
        if (resp.status >= 200 && resp.status < 300 && resp.data) {
            action?.onSuccess(resp.data as SuccessResponse<R>)
        } else {
            action?.onError(resp.data as ErrorResponse<R>)
        }
    } catch (e: any) {
        action?.onError(e.data)
    }
}
export function* GetRecurringMeasurementGroupDistributionData<P, R>(action: { type: string; payload: any; onSuccess: (response: SuccessResponse<R>) => void; onError: (response: ErrorResponse<R>) => void }): any {
    try {
        const { literal, ...payload } = action.payload
        const newPayload = structuredClone(payload)

        if (newPayload?.measurement_table_filters?.sql_query) {
            newPayload.measurement_table_filters.sql_query = btoa(newPayload.measurement_table_filters.sql_query)
        }
        const resp = yield call(CallApi.POST, APIS.EXPERIMENT.GET_RECURRING_MEASUREMENT_GROUP_DISTRIBUTION_DATA(literal), newPayload, true)
        if (resp.status >= 200 && resp.status < 300 && resp.data) {
            action?.onSuccess(resp.data as SuccessResponse<R>)
        } else {
            action?.onError(resp.data as ErrorResponse<R>)
        }
    } catch (e: any) {
        action?.onError(e.data)
    }
}
export function* updateRecurringExperiment<P, R>(action: { type: string; payload: any; onSuccess: (response: SuccessResponse<R>) => void; onError: (response: ErrorResponse<R>) => void }): any {
    try {
        const { literal, params } = action.payload
        const newPayload = structuredClone(params)

        if (newPayload?.measurement_table_filters?.sql_query) {
            newPayload.measurement_table_filters.sql_query = btoa(newPayload.measurement_table_filters.sql_query)
        }
        const resp = yield call(CallApi.POST, APIS.EXPERIMENT.UPDATE_RECURRING_EXPERIMENT(literal), newPayload, true)
        if (resp.status >= 200 && resp.status < 300 && resp.data) {
            action?.onSuccess(resp.data as SuccessResponse<R>)
        } else {
            action?.onError(resp.data as ErrorResponse<R>)
        }
    } catch (e: any) {
        action?.onError(e.data)
    }
}

export function* updateKpiDirection<P, R>(action: { type: string; payload: any; onSuccess: (response: SuccessResponse<R>) => void; onError: (response: ErrorResponse<R>) => void }): any {
    try {
        const { literal, kpis } = action.payload

        const resp = yield call(CallApi.POST, APIS.EXPERIMENT.UPDATE_KPI_DIRECTION(literal), kpis, true)
        if (resp.status >= 200 && resp.status < 300 && resp.data) {
            action?.onSuccess(resp.data as SuccessResponse<R>)
        } else {
            action?.onError(resp.data as ErrorResponse<R>)
        }
    } catch (e: any) {
        action?.onError(e.data)
    }
}

export function* getExperimentInsideMethodology<P, R>(action: { type: string; payload: any; onSuccess: (response: SuccessResponse<R>) => void; onError: (response: ErrorResponse<R>) => void }): any {
    try {
        const literal: string = action.payload.literal
        const resp = yield call(CallApi.GET, APIS.EXPERIMENT.GET_EXPERIMENT_INSIDE_METHODOLOGY(literal), action.payload, false, true)
        if (resp.status >= 200 && resp.status < 300 && resp.data) {
            action?.onSuccess(resp.data as SuccessResponse<R>)
        } else {
            action?.onError(resp.data as ErrorResponse<R>)
        }
    } catch (e: any) {
        action?.onError(e.data)
    }
}

export function* getSchedulingDetesForRecurringExperiment<P, R>(action: { type: string; payload: any; onSuccess: (response: SuccessResponse<R>) => void; onError: (response: ErrorResponse<R>) => void }): any {
    try {
        const { literal, ...payload } = action.payload
        const resp = yield call(CallApi.POST, APIS.EXPERIMENT.GET_SCHEDULE_DATES_FOR_RECURRING_EXPERIMENT(literal), payload, false)
        if (resp.status >= 200 && resp.status < 300 && resp.data) {
            action?.onSuccess(resp.data as SuccessResponse<R>)
        } else {
            action?.onError(resp.data as ErrorResponse<R>)
        }
    } catch (e: any) {
        action?.onError(e.data)
    }
}

export function* deleteRecurringExperimentChild<P, R>(action: { type: string; payload: any; onSuccess: (response: SuccessResponse<R>) => void; onError: (response: ErrorResponse<R>) => void }): any {
    try {
        const { literal, ...payload } = action.payload
        const resp = yield call(CallApi.DELETE, APIS.EXPERIMENT.DELETE_RECURRING_EXPERIMENT_CHILD(literal), payload, true)
        if (resp.status >= 200 && resp.status < 300 && resp.data) {
            action?.onSuccess(resp.data as SuccessResponse<R>)
        } else {
            action?.onError(resp.data as ErrorResponse<R>)
        }
    } catch (e: any) {
        action?.onError(e.data)
    }
}

export function* updateRecurringDateConfig<P, R>(action: { type: string; payload: any; onSuccess: (response: SuccessResponse<R>) => void; onError: (response: ErrorResponse<R>) => void }): any {
    try {
        const { literal, ...payload } = action.payload
        const resp = yield call(CallApi.POST, APIS.EXPERIMENT.UPDATE_RECURRING_DATE_CONFIG(literal), payload, true)
        if (resp.status >= 200 && resp.status < 300 && resp.data) {
            action?.onSuccess(resp.data as SuccessResponse<R>)
        } else {
            action?.onError(resp.data as ErrorResponse<R>)
        }
    } catch (e: any) {
        action?.onError(e.data)
    }
}
export function* deleteExperimentChild<P, R>(action: { type: string; payload: any; onSuccess: (response: SuccessResponse<R>) => void; onError: (response: ErrorResponse<R>) => void }): any {
    try {
        const literal: string = action.payload.literal
        const resp = yield call(CallApi.DELETE, APIS.EXPERIMENT.DELETE_EXPERIMENT_CHILD(literal), {}, true)
        if (resp.status >= 200 && resp.status < 300 && resp.data) {
            action?.onSuccess(resp.data as SuccessResponse<R>)
        } else {
            action?.onError(resp.data as ErrorResponse<R>)
        }
    } catch (e: any) {
        action?.onError(e.data)
    }
}
export function* getExperimentMasterDecision<P, R>(action: { type: string; payload: any; onSuccess: (response: SuccessResponse<R>) => void; onError: (response: ErrorResponse<R>) => void }): any {
    try {
        const resp = yield call(CallApi.GET, APIS.EXPERIMENT.GET_EXPERIMENT_MASTER_DECISION, action.payload, false, true)
        if (resp.status >= 200 && resp.status < 300 && resp.data) {
            action?.onSuccess(resp.data as SuccessResponse<R>)
        } else {
            action?.onError(resp.data as ErrorResponse<R>)
        }
    } catch (e: any) {
        action?.onError(e.data)
    }
}
export function* saveExperimentDecision<P, R>(action: { type: string; payload: any; onSuccess: (response: SuccessResponse<R>) => void; onError: (response: ErrorResponse<R>) => void }): any {
    try {
        const resp = yield call(CallApi.POST, APIS.EXPERIMENT.SAVE_EXPERIMENT_DECISION, action.payload, true)
        if (resp.status >= 200 && resp.status < 300 && resp.data) {
            action?.onSuccess(resp.data as SuccessResponse<R>)
        } else {
            action?.onError(resp.data as ErrorResponse<R>)
        }
    } catch (e: any) {
        action?.onError(e.data)
    }
}
export function* editExperiment<P, R>(action: { type: string; payload: any; onSuccess: (response: SuccessResponse<R>) => void; onError: (response: ErrorResponse<R>) => void }): any {
    try {
        const { literal, ...payload } = action.payload
        const resp = yield call(CallApi.PATCH, APIS.EXPERIMENT.EXPERIMENT_EDIT(literal), payload, true)
        if (resp.status >= 200 && resp.status < 300 && resp.data) {
            action?.onSuccess(resp.data as SuccessResponse<R>)
        } else {
            action?.onError(resp.data as ErrorResponse<R>)
        }
    } catch (e: any) {
        action?.onError(e.data)
    }
}

export function* downloadRctGZipResults<P, R>(action: {
    type: string
    payload: {
        literal: string
        [name: string]: any
    }
    onSuccess: (response: SuccessResponse<R>) => void
    onError: (response: ErrorResponse<R>) => void
}): any {
    try {
        const { literal, formData, ...queryParams } = action.payload
        const requestParams: any = formData ?? queryParams
        const endpoint = APIS.EXPERIMENT.DOWNLOAD_RCT_GZIP_RESULTS(literal)

        const resp = yield call(CallApi.GETDOWNLOAD, endpoint, requestParams, true, true)

        if (resp?.status >= 200 && resp?.status < 300 && resp?.data) {
            action?.onSuccess(resp.data as SuccessResponse<R>)
            return
        }

        action?.onError((resp?.data ?? resp) as ErrorResponse<R>)
    } catch (e: any) {
        // action?.onError(e.data)
        // ✅ CHANGED: Added null check and fallback error object
        action?.onError(
            e?.response?.data ||
                e?.data || {
                    message: e?.message || 'Failed to download RCT GZIP results',
                    status_code: e?.status || 500,
                    success: false,
                },
        )
    }
}
